<template>
<div>
  <v-container fluid class="my-5">
    <v-layout row justify-center>
      <div style="width: 95%;">
          {{ $t('views.adminReferenceModels.title') }}
      </div>
      <v-data-table dense :headers="headers" :items="referenceModels" style="width: 95%;" :search="search" :custom-sort="customSort">
        <template v-slot:top>
          <v-toolbar flat color="white">
            <v-dialog v-model="createDialog" fullscreen hide-overlay transition="dialog-bottom-transition" persistent>
              <template v-slot:activator="{ on: dialog }">
                <v-btn small color="#212121" class="white--text" v-on="{...dialog}">
                  <v-icon class="align-center" color="primary">mdi-plus-thick</v-icon>
                  <span class="pl-2">{{ $t('views.adminReferenceModels.createButton') }}</span>
                </v-btn>
              </template>
              <create-reference-model v-if="createDialog" @close-dialog="createDialog = false; editItem = null;" :created-by="authUser.uid" :existing-models="existingReferenceModels" :editing="editItem" />
            </v-dialog>
            <v-spacer></v-spacer>
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              :label="$t('views.adminReferenceModels.searchLabel')"
              single-line
              hide-details
            ></v-text-field>
          </v-toolbar>
        </template>
        <template v-slot:item.category="{ item }">
          {{ item.config.category }}
        </template>
        <template v-slot:item.date_created="{ item }">
          {{ dateModified(item) }}
        </template>
        <template v-slot:item.model_status="{ item }">
          <model-status :model="item"/>
        </template>
        <template v-slot:item.action="{ item }">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn icon v-on="on" @click.stop="createDialog = true; editItem = item">
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
            </template>
            {{ $t('views.adminReferenceModels.tooltips.edit') }}
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn icon v-on="on" @click.stop="deleteDialog = true; deleteItem = item">
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
              <v-dialog v-model="deleteDialog" max-width="400">
                <v-card>
                  <v-card-title class="headline">{{ $t('views.adminReferenceModels.deleteDialog.title') }}</v-card-title>
                  <v-card-text>
                    {{ $t('views.adminReferenceModels.deleteDialog.message') }} <b>{{ deleteItem.id }}</b>
                    <br/>
                    {{ $t('views.adminReferenceModels.deleteDialog.confirmation') }}
                  </v-card-text>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="gray darken-1" text @click="deleteDialog = false">{{ $t('views.adminReferenceModels.deleteDialog.cancel') }}</v-btn>
                    <v-btn color="red darken-1" text @click="triggerDelete(deleteItem.id); deleteDialog = false">{{ $t('views.adminReferenceModels.deleteDialog.delete') }}</v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </template>
            {{ $t('views.adminReferenceModels.tooltips.delete') }}
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn icon v-on="on" @click.stop="updateDialog = true; updateItem = item">
                <v-icon small>mdi-file-replace</v-icon>
              </v-btn>
              <v-dialog v-model="updateDialog" max-width="400">
                <v-card>
                  <v-card-title class="headline">{{ $t('views.adminReferenceModels.updateDialog.title') }}</v-card-title>
                  <v-card-text>
                    {{ $t('views.adminReferenceModels.updateDialog.message') }} <b>{{ updateItem.id }}</b>
                    <br/>
                    {{ $t('views.adminReferenceModels.updateDialog.confirmation') }}
                  </v-card-text>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="gray darken-1" text @click="updateDialog = false">{{ $t('views.adminReferenceModels.updateDialog.cancel') }}</v-btn>
                    <v-btn color="red darken-1" text @click="updateCustomModels(updateItem); updateDialog = false">{{ $t('views.adminReferenceModels.updateDialog.update') }}</v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </template>
            {{ $t('views.adminReferenceModels.tooltips.updateCustomModels') }}
          </v-tooltip>
        </template>
        <template v-slot:item.disabled="{ item }">
          <v-simple-checkbox
            :ripple="false"
            v-model="item.disabled"
            @input="triggerDisable(item.id, item.disabled)"
          ></v-simple-checkbox>
        </template>
      </v-data-table>
    </v-layout>
  </v-container>
</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import CreateReferenceModel from '@/components/CreateReferenceModel.vue'
import ModelStatus from '@/components/ModelStatus.vue'
import _ from 'lodash'

export default {
  data () {
    return {
      createDialog: false,
      deleteDialog: false,
      deleteItem: '',
      updateDialog: false,
      updateItem: '',
      editItem: null,
      search: ''
    }
  },
  methods: {
    ...mapActions('admin', [
      'loadReferenceModels',
      'deleteReferenceModel',
      'disableReferenceModel'
    ]),
    ...mapActions('manager', [
      'loadCustomModels',
      'updateCustomModelConfig'
    ]),
    triggerDelete (id) {
      this.deleteReferenceModel({
        codename: id
      })
    },
    triggerDisable (id, disabled) {
      console.log('Id: ', id)
      console.log('Disabled: ', disabled)
      this.disableReferenceModel({
        codename: id,
        disabled: disabled
      })
    },
    dateModified (item) {
      const dateModified = item.date_edited ? item.date_edited : item.date_created
      return dateModified.toDate().toString().slice(4, 24)
    },
    customSort: function (items, sortBy, sortDesc, locale, customSorters) {
      const sortCol = sortBy[0]
      if (sortCol === 'date_created') {
        if (sortDesc[0]) {
          items.sort((a, b) => {
            let aDate = a.date_created
            let bDate = b.date_created
            if ('date_edited' in a) {
              aDate = a.date_edited
            }
            if ('date_edited' in b) {
              bDate = b.date_edited
            }
            return aDate - bDate
          })
        } else {
          items.sort((a, b) => {
            let aDate = a.date_created
            let bDate = b.date_created
            if ('date_edited' in a) {
              aDate = a.date_edited
            }
            if ('date_edited' in b) {
              bDate = b.date_edited
            }
            return bDate - aDate
          })
        }
      } else {
        if (!sortDesc[0]) {
          items.sort((a, b) => {
            return _.get(sortCol === 'category' ? b.config : b, sortCol, '').toString().localeCompare(_.get(sortCol === 'category' ? a.config : a, sortCol, '').toString(), undefined, {
              numeric: true,
              sensitivity: 'base'
            })
          })
        } else {
          items.sort((a, b) => {
            return _.get(sortCol === 'category' ? a.config : a, sortCol, '').toString().localeCompare(_.get(sortCol === 'category' ? b.config : b, sortCol, '').toString(), undefined, {
              numeric: true,
              sensitivity: 'base'
            })
          })
        }
      }
      return items
    },
    updateCustomModels (item) {
      const models = this.customModels.map(model => {
        // Get only Custom Models corresponding to this Reference Model
        if (model.reference_model.codename === item.id) {
          return {
            id: model.id,
            name: model.name,
            reference_model: Object.assign({}, model.reference_model)
          }
        }
      }).filter(Boolean) // Remove undefined values from the array
      console.log(`Number of models to be updated: ${models.length}`)
      console.log(models)
      let updated = 0
      models.forEach(model => {
        console.log(`Updating config for model: ${model.name}`)
        const config = item.config
        const referenceModel = model.reference_model
        const newReferenceModel = Object.assign({}, referenceModel, { config: config })
        this.updateCustomModelConfig({
          client: model.id,
          reference_model: newReferenceModel
        }).then(() => {
          console.log(`✔ Model config updated: ${model.name}`)
          updated++
          console.log(`Models updated: ${updated}/${models.length}`)
        }).catch(error => {
          console.log(`✘ Error updating model: ${model.name}`)
          console.log(error)
        })
      })
    }
  },
  computed: {
    ...mapGetters('admin', [
      'referenceModels'
    ]),
    ...mapGetters('auth', [
      'authUser'
    ]),
    ...mapGetters('manager', [
      'customModels'
    ]),
    existingReferenceModels: function () {
      var models = []
      this.referenceModels.forEach(element => {
        models.push(element.id)
      })
      return models
    },
    headers: function () {
      let headers = [
        { text: this.$t('views.adminReferenceModels.headers.categoryName'), value: 'category' },
        { text: this.$t('views.adminReferenceModels.headers.codename'), value: 'codename' },
        { text: this.$t('views.adminReferenceModels.headers.pkl'), value: 'pkl.name' },
        { text: this.$t('views.adminReferenceModels.headers.pklSize'), value: 'pkl.size' },
        { text: this.$t('views.adminReferenceModels.headers.modified'), value: 'date_created' },
        { text: this.$t('views.adminReferenceModels.headers.actions'), value: 'action', sortable: false, width: '140px' },
        { text: this.$t('views.adminReferenceModels.headers.disabled'), value: 'disabled' }
      ]
      // Duplicated in Home.vue and App.vue
      // TODO: Unify

      // Status column only hidden in production for the peace of mind of admins
      if (window.location.hostname !== 'predixon.noisegrasp.com' && window.location.hostname !== 'app.noisegrasp.com' && window.location.hostname !== 'outflink.noisegrasp.com' && window.location.hostname !== 'deloitte.noisegrasp.com' && window.location.hostname !== 'nisum.noisegrasp.com' && window.location.hostname !== 'mckinsey.noisegrasp.com' && window.location.hostname !== 'comscore.noisegrasp.com') {
        headers.splice(headers.length - 2, 0, { text: this.$t('views.adminReferenceModels.headers.status'), value: 'model_status', width: '1px' })
      }
      return headers
    }
  },
  components: {
    CreateReferenceModel,
    ModelStatus
  },
  created: function () {
    this.loadReferenceModels()
    this.loadCustomModels()
  }
}
</script>
